import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { sessionAPI } from '../../api/client'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

interface RewardState {
  sessionId: string
  minutes: number
  skillFocus?: string
}

/** Standalone session completion / rewards screen */
export default function SessionCompletionRewardsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? {}) as Partial<RewardState>

  const sessionId = state.sessionId ?? ''
  const minutes = state.minutes ?? 15
  const skillFocus = state.skillFocus ?? 'listening'

  const [confetti, setConfetti] = useState(false)

  const complete = useMutation({
    mutationFn: () => sessionAPI.complete(sessionId),
  })

  useEffect(() => {
    if (sessionId) complete.mutate()
    setTimeout(() => setConfetti(true), 300)
  }, []) // eslint-disable-line

  const xpGained = complete.data?.xp ?? 0
  const xpPct = 75
  const milestones: string[] = complete.data?.newMilestones ?? []

  const SKILL_COLORS: Record<string, string> = {
    listening: '#006571', reading: '#004be2', writing: '#8e00d5', speaking: '#b41340',
  }
  const accentColor = SKILL_COLORS[skillFocus] ?? '#004be2'

  return (
    <div style={{ minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", position: 'relative', overflow: 'hidden' }}>
      {/* Ambient glow */}
      <div style={{ position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', height: '37.5rem', background: `linear-gradient(to bottom, ${accentColor}18, transparent)`, filter: 'blur(7.5rem)', pointerEvents: 'none', zIndex: 0 }} />

      {/* Confetti dots */}
      {confetti && Array.from({ length: 18 }).map((_, i) => (
        <div key={i} style={{
          position: 'fixed', top: `${Math.random() * 40}%`, left: `${Math.random() * 100}%`,
          width: `${6 + Math.random() * 8}px`, height: `${6 + Math.random() * 8}px`,
          borderRadius: Math.random() > 0.5 ? '50%' : '0',
          background: ['#004be2', '#8e00d5', '#b41340', '#006571', '#f59e0b'][i % 5],
          animation: `confetti-fall ${1.5 + Math.random() * 2}s ease-in forwards`,
          animationDelay: `${Math.random() * 0.8}s`,
          zIndex: 0, pointerEvents: 'none',
        }} />
      ))}

      <main style={{ position: 'relative', zIndex: 1, maxWidth: '64rem', margin: '0 auto', padding: '3rem 1.5rem 6rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Hero */}
        <section style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0.875rem', marginBottom: '1.5rem', background: `${accentColor}18`, borderRadius: '9999px' }}>
            <span className="material-symbols-outlined" style={{ color: accentColor, fontSize: '3rem', fontVariationSettings: "'FILL' 1" }}>celebration</span>
          </div>
          <H style={{ display: 'block', fontSize: 'clamp(2.5rem, 7vw, 4.5rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 0.95, marginBottom: '1rem' }}>
            Session Complete!
          </H>
          <p style={{ color: '#535b71', fontSize: '1.125rem', fontWeight: 500, maxWidth: '28rem', margin: '0 auto' }}>
            Tuyệt vời! Bạn vừa nâng cấp kỹ năng <strong style={{ color: accentColor }}>{skillFocus}</strong> thêm {minutes} phút nữa.
          </p>
        </section>

        {/* Bento grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '1.5rem', width: '100%' }}>
          {/* XP Circle */}
          <div style={{ gridColumn: 'span 7', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(24px)', borderRadius: '1.5rem', padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.04)' }}>
            <div style={{ position: 'relative', width: '12rem', height: '12rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <svg width="192" height="192" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="96" cy="96" r="80" fill="none" stroke="#d1dcff" strokeWidth="12" />
                <circle cx="96" cy="96" r="80" fill="none" stroke={accentColor} strokeWidth="12"
                  strokeDasharray={`${2 * Math.PI * 80}`}
                  strokeDashoffset={`${2 * Math.PI * 80 * (1 - xpPct / 100)}`}
                  strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1.2s ease' }}
                />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <span style={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: `${accentColor}99`, marginBottom: '0.25rem' }}>Total XP</span>
                <H style={{ fontSize: '2.5rem', fontWeight: 900, color: accentColor, lineHeight: 1 }}>2,450</H>
                {xpGained > 0 && <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#535b71' }}>+{xpGained} kiếm được</span>}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>Level 14: Fluency Pioneer</H>
              <span style={{ fontSize: '0.875rem', color: '#535b71' }}>350 XP đến Level 15</span>
            </div>
          </div>

          {/* Streak card */}
          <div style={{ gridColumn: 'span 5', background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`, borderRadius: '1.5rem', padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', color: 'white', boxShadow: `0 20px 40px ${accentColor}33` }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', marginBottom: '1rem' }}>Daily Streak</p>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '6rem', display: 'block', fontVariationSettings: "'FILL' 1", color: 'rgba(255,255,255,0.9)' }}>local_fire_department</span>
                <div style={{ position: 'absolute', bottom: '-0.5rem', left: '50%', transform: 'translateX(-50%)', background: 'white', color: accentColor, borderRadius: '9999px', padding: '0.25rem 1rem', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900, fontSize: '1.25rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                  14
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <H style={{ display: 'block', fontSize: '1.375rem', fontWeight: 900, color: 'white', marginBottom: '0.5rem' }}>Streak Updated!</H>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem' }}>Bạn thuộc top 5% học viên tháng này!</p>
            </div>
          </div>

          {/* Session summary */}
          <div style={{ gridColumn: 'span 12', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(24px)', borderRadius: '1.5rem', padding: '1.5rem 2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
            <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', marginBottom: '1.25rem' }}>📊 Tóm tắt phiên học</H>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
              {[
                { icon: 'timer', label: 'Thời gian', value: `${minutes} phút`, color: '#004be2' },
                { icon: 'quiz', label: 'Câu hỏi', value: '12 câu', color: '#8e00d5' },
                { icon: 'check_circle', label: 'Đúng', value: '9/12', color: '#006571' },
                { icon: 'trending_up', label: 'Cải thiện', value: '+8%', color: '#b45309' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '1rem', background: `${item.color}08`, borderRadius: '1rem' }}>
                  <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem', background: `${item.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span className="material-symbols-outlined" style={{ color: item.color, fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
                  </div>
                  <div>
                    <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 900, color: item.color }}>{item.value}</H>
                    <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>{item.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Milestones */}
          {milestones.length > 0 && (
            <div style={{ gridColumn: 'span 12', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(24px)', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
              <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', marginBottom: '1rem' }}>🏅 Thành tựu mới</H>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {milestones.map((m: string) => (
                  <span key={m} style={{ padding: '0.5rem 1.25rem', borderRadius: '9999px', background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`, color: 'white', fontSize: '0.875rem', fontWeight: 700 }}>
                    {m.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Next session suggestion */}
          <div style={{ gridColumn: 'span 12', background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem 2rem' }}>
            <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', marginBottom: '1rem', color: '#004be2' }}>💡 Gợi ý tiếp theo</H>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {[
                { label: 'Ôn từ vựng', icon: 'book_2', path: '/vocab' },
                { label: 'Luyện kỹ năng yếu', icon: 'fitness_center', path: '/session' },
                { label: 'Xem tiến độ', icon: 'leaderboard', path: '/progress' },
              ].map(item => (
                <button key={item.label} onClick={() => navigate(item.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderRadius: '0.875rem', border: 'none', cursor: 'pointer', background: 'white', color: '#272e42', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.875rem', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', color: '#004be2', fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={{ gridColumn: 'span 12', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/session')}
              style={{ padding: '1.125rem 3rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`, color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', boxShadow: `0 8px 24px ${accentColor}44`, display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>replay</span>
              Học thêm
            </button>
            <button onClick={() => navigate('/dashboard')}
              style={{ padding: '1.125rem 2.5rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}>
              Về trang chủ
            </button>
          </div>
        </div>
      </main>

      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  )
}
